import React, { useState, useEffect, Fragment } from 'react';
import { useParams, Link } from "react-router-dom";
import apiClient from "../api/client";
import { useCart } from "../context/CartContext";
import { Tab } from '@headlessui/react';
import { type Plugin as BasePlugin } from "../context/PluginContext";
import { PluginBannerPlaceholder } from "../components/common/PluginBannerPlaceholder";
import { APP_CONFIG } from '../config/constants';

// Tipos
interface PluginDetailPagePlugin extends BasePlugin {
    overview?: string;
    commands?: string;
    permissions?: string;
    additionalFiles?: string;
    demoVideoUrl?: string;
    versions?: { id: number; version: string; changelog: string; createdAt: string; }[];
}

const StarRating = ({ rating = 0 }: { rating: number }) => (
    <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
            <svg key={index} className={`w-5 h-5 ${index < Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        ))}
    </div>
);

export default function PluginDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const { addToCart, cartItems } = useCart();
    const [plugin, setPlugin] = useState<PluginDetailPagePlugin | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchPlugin() {
            if (!slug) return;
            setLoading(true);
            setError(null);
            try {
                const response = await apiClient.get(`/storefront/plugins/${slug}`);
                setPlugin(response.data);
            } catch (err: any) {
                setError(err.response?.data?.error || "Plugin não encontrado.");
            } finally {
                setLoading(false);
            }
        }
        fetchPlugin();
    }, [slug]);

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8 animate-pulse">
                {/* ... (Skeleton JSX) ... */}
            </div>
        );
    }

    if (error || !plugin) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <h1 className="text-4xl font-bold">{error || "Plugin não encontrado!"}</h1>
                <p className="mt-4">A URL pode estar incorreta ou este plugin não está mais disponível.</p>
                <Link to="/plugins" className="mt-6 inline-block bg-brand-blue text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700">Ver outros plugins</Link>
            </div>
        );
    }

    const isInCart = cartItems.some(item => item.id === plugin.id);

    const formatArray = (arr: string[] | undefined) => {
        if (!arr || arr.length === 0) return 'N/A';
        return arr.join(', ');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Hero Section com Banner */}
            <div className="relative bg-gradient-to-r from-brand-blue to-blue-700 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                    <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                </div>

                <div className="container mx-auto px-4 py-12 relative">
                    <div className="max-w-4xl">
                        {/* Título e Informações */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <Link to="/plugins" className="text-blue-100 hover:text-white transition-colors flex items-center gap-1 text-sm font-medium">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Voltar para plugins
                                </Link>
                            </div>

                            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 drop-shadow-lg">
                                {plugin.name}
                            </h1>

                            <p className="text-xl text-blue-100 mb-6 max-w-3xl">
                                {plugin.tagline}
                            </p>

                            {/* Stats em linha */}
                            <div className="flex flex-wrap gap-4 mb-6">
                                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                                    <StarRating rating={Number(plugin.rating) || 0} />
                                    <span className="text-white font-semibold">{(Number(plugin.rating) || 0).toFixed(1)}</span>
                                    <span className="text-blue-100 text-sm">({plugin.reviewCount || 0})</span>
                                </div>

                                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                                    <svg className="w-5 h-5 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    <span className="text-white font-semibold">{plugin.downloadCount || 0}</span>
                                    <span className="text-blue-100 text-sm">downloads</span>
                                </div>

                                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                                    <svg className="w-5 h-5 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    <span className="text-white font-semibold">{plugin.currentVersion || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ondinha decorativa */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg className="w-full h-8 text-gray-50" preserveAspectRatio="none" viewBox="0 0 1200 120" fill="currentColor">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
                    </svg>
                </div>
            </div>

            {/* Conteúdo Principal */}
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sidebar - Compra e Informações */}
                    <aside className="lg:col-span-1 space-y-6">
                        {/* Card de Compra */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 sticky top-4">
                            <div className="bg-gradient-to-br from-gray-50 to-white p-6 border-b border-gray-200">
                                <div className="text-center mb-4">
                                    <p className="text-sm text-gray-600 mb-2">Preço</p>
                                    <p className="text-5xl font-extrabold text-brand-blue">
                                        {plugin.price === 0 ? 'Grátis' : `R$ ${plugin.price.toFixed(2)}`}
                                    </p>
                                </div>
                                <button
                                    onClick={() => addToCart(plugin)}
                                    disabled={isInCart}
                                    className="w-full bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold px-6 py-4 rounded-xl text-lg hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center gap-2"
                                >
                                    {isInCart ? (
                                        <>
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Adicionado ao Carrinho
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            Adicionar ao Carrinho
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Especificações
                                </h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                        <svg className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-700">Versão Atual</p>
                                            <p className="text-gray-600">{plugin.currentVersion || 'N/A'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                        <svg className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-700">Compatível com</p>
                                            <p className="text-gray-600">{formatArray(plugin.compatibleVersions)}</p>
                                        </div>
                                    </div>


                                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                        <svg className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-700">Dependências</p>
                                            <p className="text-gray-600">{formatArray(plugin.dependencies)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Conteúdo Principal - Tabs */}
                    <div className="lg:col-span-2">

                        {/* Tabs de Conteúdo */}
                        <Tab.Group as="div">
                            <Tab.List className="flex flex-wrap gap-2 mb-6">
                                <Tab className={({ selected }) => `
                                    px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200
                                    ${selected 
                                        ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg transform scale-105' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow border border-gray-200'
                                    }
                                `}>
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Visão Geral
                                    </span>
                                </Tab>
                                <Tab className={({ selected }) => `
                                    px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200
                                    ${selected 
                                        ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg transform scale-105' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow border border-gray-200'
                                    }
                                `}>
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Comandos
                                    </span>
                                </Tab>
                                <Tab className={({ selected }) => `
                                    px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200
                                    ${selected 
                                        ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg transform scale-105' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow border border-gray-200'
                                    }
                                `}>
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        Permissões
                                    </span>
                                </Tab>
                                <Tab className={({ selected }) => `
                                    px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200
                                    ${selected 
                                        ? 'bg-gradient-to-r from-brand-blue to-blue-600 text-white shadow-lg transform scale-105' 
                                        : 'bg-white text-gray-700 hover:bg-gray-100 shadow border border-gray-200'
                                    }
                                `}>
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Vídeo Demo
                                    </span>
                                </Tab>
                            </Tab.List>

                            <Tab.Panels>
                                <Tab.Panel className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200 min-h-[400px]">
                                    <div className="prose max-w-none">
                                        {plugin.overview ? (
                                            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{plugin.overview}</div>
                                        ) : (
                                            <div className="text-center py-12">
                                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <p className="text-gray-500">Nenhuma visão geral detalhada disponível.</p>
                                            </div>
                                        )}
                                    </div>
                                </Tab.Panel>

                                <Tab.Panel className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200 min-h-[400px]">
                                    <div className="prose max-w-none">
                                        {plugin.commands ? (
                                            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap font-mono text-sm">{plugin.commands}</div>
                                        ) : (
                                            <div className="text-center py-12">
                                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-gray-500">Nenhum comando documentado.</p>
                                            </div>
                                        )}
                                    </div>
                                </Tab.Panel>

                                <Tab.Panel className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200 min-h-[400px]">
                                    <div className="prose max-w-none">
                                        {plugin.permissions ? (
                                            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap font-mono text-sm">{plugin.permissions}</div>
                                        ) : (
                                            <div className="text-center py-12">
                                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <p className="text-gray-500">Nenhuma permissão documentada.</p>
                                            </div>
                                        )}
                                    </div>
                                </Tab.Panel>

                                <Tab.Panel className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200 min-h-[400px]">
                                    {plugin.demoVideoUrl ? (
                                        <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                                            <iframe
                                                src={plugin.demoVideoUrl}
                                                title={`Vídeo de demonstração do plugin ${plugin.name}`}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                className="w-full h-full rounded-xl"
                                                style={{ minHeight: '400px' }}
                                            ></iframe>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="text-gray-500">Nenhum vídeo de demonstração disponível.</p>
                                        </div>
                                    )}
                                </Tab.Panel>
                            </Tab.Panels>
                        </Tab.Group>
                    </div>
                </div>
            </div>
        </div>
    );
}